import React, { useState, useEffect, useCallback } from 'react';
import { Box, Container, Tabs, Tab, CircularProgress, Alert, Typography } from '@mui/material';
import P1P2ChangeIncidents from './P1P2ChangeIncidents';
import P1P2ChangeDashboard from './P1P2ChangeDashboard';
import { useMsal } from '@azure/msal-react';
import { authFetch } from '../services/authFetch';

interface IncidentData {
  id: string;
  application_id: string | null;
  configuration_item: string;
  short_description: string;
  resolved: string | null;
  opened: string;
  priority: string;
  brand: string | null;
  mvc: boolean;
  platform: string;
  portfolio: string;
  caused_by_platform: string;
  caused_by_portfolio: string;
  caused_by_change: boolean | null;
  change_type: string | null;
  change_process: string | null;
  triggering_change_request: string[];
  observations: string | null;
  root_cause_code: string | null;
  root_cause_sub_code: string | null;
  ttr_seconds: number | null;
  ttd_seconds: number | null;
}

interface OutageData {
  outage_number: string;
  configuration_item: string;
  outage_type: string;
  start_time: string;
  end_time: string;
  duration: string;
  task_number: string;
}

interface OutageDataResponse {
  status: string;
  data: OutageData[];
}

interface P1P2DataResponse {
  data: IncidentData[];
  status: number;
}

const ChangeAnalyticsAndTrends: React.FC = () => {
  const { accounts } = useMsal();
  const isAuthorized = new Set([
    'shilpitha.swarna@maersk.com',
    'k.harish@maersk.com',
    'nitish.yn@maersk.com',
  ]).has((accounts[0]?.username || '').toLowerCase());
  const [selectedTab, setSelectedTab] = useState(0);
  const [incidents, setIncidents] = useState<IncidentData[]>([]);
  const [outageData, setOutageData] = useState<OutageData[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const hasFetchedRef = React.useRef(false);

  const FLUX_BACKEND_URL = process.env.REACT_APP_FLUX_BACKEND_URL;

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
  };

  // Fetch P1/P2 incident data using authFetch (injects Azure token)
  const fetchP1P2Data = useCallback(async (): Promise<void> => {
    try {
      const response = await authFetch(`${FLUX_BACKEND_URL}/api/change_data/p1p2Incidents`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch P1/P2 data: ${response.statusText}`);
      }

      const result: P1P2DataResponse = await response.json();

      if (result.status === 200 && result.data) {
        setIncidents(result.data);
        setError(null);
      } else {
        throw new Error('Invalid response format');
      }
    } catch (error) {
      console.error('Error fetching P1/P2 data:', error);
      setError(error instanceof Error ? error.message : 'Failed to fetch incident data');
      throw error;
    }
  }, [FLUX_BACKEND_URL]);

  // Fetch outage data using authFetch (injects Azure token)
  const fetchOutageData = useCallback(async (): Promise<void> => {
    try {
      const response = await authFetch(`${FLUX_BACKEND_URL}/api/outage_data/all`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch outage data: ${response.statusText}`);
      }

      const result: OutageDataResponse = await response.json();

      if (result.status === 'success' && result.data) {
        setOutageData(result.data);
      } else {
        throw new Error('Invalid response format');
      }
    } catch (error) {
      console.error('Error fetching outage data:', error);
      // Don't throw error - outage data is supplementary, not critical
    }
  }, [FLUX_BACKEND_URL]);

  // Initialize data fetching when component mounts
  useEffect(() => {
    const initializeData = async () => {
      // Prevent double fetching with ref
      if (hasFetchedRef.current) {
        return;
      }

      hasFetchedRef.current = true;
      setLoading(true);
      setError(null);

      try {
        // Fetch both P1P2 data and outage data in parallel
        await Promise.all([
          fetchP1P2Data(),
          fetchOutageData()
        ]);
      } catch (error) {
        console.error('Error initializing data:', error);
        setError(error instanceof Error ? error.message : 'Failed to initialize data');
      } finally {
        setLoading(false);
      }
    };

    if (isAuthorized && accounts.length > 0 && !hasFetchedRef.current) {
      initializeData();
    }
  }, [accounts, fetchOutageData, fetchP1P2Data, isAuthorized]);

  const handleRefresh = async () => {
    setLoading(true);
    setError(null);
    try {
      await Promise.all([
        fetchP1P2Data(),
        fetchOutageData()
      ]);
    } catch (error) {
      console.error('Error refreshing data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!isAuthorized) {
    return (
      <Container maxWidth="xl">
        <Alert severity="warning" sx={{ mt: 2 }}>
          This dashboard is restricted to authorized users.
        </Alert>
      </Container>
    );
  }

  if (loading && incidents.length === 0) {
    return (
      <Container maxWidth="xl">
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error && incidents.length === 0) {
    return (
      <Container maxWidth="xl">
        <Box sx={{ mt: 2 }}>
          <Alert severity="error">{error}</Alert>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl">
      <Box sx={{ minHeight: '100vh', pt: 1, pb: 3 }}>
        <Box sx={{ textAlign: 'start', mb: 2 }}>
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#003366' }}>
            P1/P2 Due to Change
          </Typography>
        </Box>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs
            value={selectedTab}
            onChange={handleTabChange}
            sx={{
              '& .MuiTab-root': {
                fontSize: '1rem',
                fontWeight: 600,
                textTransform: 'none',
                minWidth: 200,
              },
              '& .Mui-selected': {
                color: '#42B0D5',
              },
              '& .MuiTabs-indicator': {
                backgroundColor: '#42B0D5',
                height: 3,
              },
            }}
          >
            <Tab label="Analytics" />
            <Tab label="Trends" />
          </Tabs>
        </Box>

        {selectedTab === 0 && (
          <P1P2ChangeIncidents
            incidents={incidents}
            outageData={outageData}
            loading={loading}
            error={error}
            onRefresh={handleRefresh}
          />
        )}
        {selectedTab === 1 && (
          <P1P2ChangeDashboard
            incidents={incidents}
            outageData={outageData}
            loading={loading}
            error={error}
            onRefresh={handleRefresh}
          />
        )}
      </Box>
    </Container>
  );
};

export default ChangeAnalyticsAndTrends;
